import { typographyTokens } from '../theme/typographyTokens';
import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, Link, useTheme } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useGetSepBannerSettingsQuery } from '../services/appSlice/appSlice';

export type Banner = { name: string; value: string; id?: string | number };

interface BannerCarouselProps {
  sx?: object;
}

export function BannerCarousel({ sx }: BannerCarouselProps) {
  const { data: sepBanners } = useGetSepBannerSettingsQuery();
  const activeBanners = (sepBanners || []).filter((b: Banner) => b.value.endsWith(' (active)'));
  const [openBanners, setOpenBanners] = useState<Record<string, boolean>>({});
  const [hideBanners, setHideBanners] = useState(false);
  const [current, setCurrent] = useState(0);
  const theme = useTheme();

  useEffect(() => {
    if (activeBanners && activeBanners.length > 0) {
      setOpenBanners((prev) => {
        const newState: Record<string, boolean> = { ...prev };
        activeBanners.forEach((b: Banner) => {
          const id = String(b.id);
          if (newState[id] === undefined) newState[id] = true;
        });
        Object.keys(newState).forEach((id) => {
          if (!activeBanners.find((b: Banner) => String(b.id) === id)) delete newState[id];
        });
        return newState;
      });
    }
  }, [sepBanners]);

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `@keyframes fadeInBanner { from { opacity: 0; transform: translateY(-16px);} to { opacity: 1; transform: none; } }`;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  useEffect(() => {
    if (activeBanners.length < 2) return;
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % activeBanners.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [activeBanners.length]);

  const bannersToShow = activeBanners.filter((b: Banner) => openBanners[String(b.id)] !== false);
  const banner = bannersToShow[current] || bannersToShow[0];
  if (!banner || hideBanners) return null;

  // Color extraction
  let bgGradient = 'linear-gradient(90deg, #FFF700 0%, #FFF9C4 100%)';
  let borderColor = '#ffe066';
  let boxShadow = '0 4px 16px 0 rgba(255, 215, 0, 0.10)';
  const colorMatches = banner.name.match(/(yellow|red|green)/gi);
  const color = colorMatches ? colorMatches[colorMatches.length - 1].toLowerCase() : banner.name;
  if (color === 'red') {
    bgGradient = 'linear-gradient(90deg, #ffcccc 0%, #ffeaea 100%)';
    borderColor = '#ffb3b3';
    boxShadow = '0 4px 16px 0 rgba(255, 0, 0, 0.10)';
  } else if (color === 'green') {
    bgGradient = 'linear-gradient(90deg, #c8e6c9 0%, #e8f5e9 100%)';
    borderColor = '#a5d6a7';
    boxShadow = '0 4px 16px 0 rgba(76, 175, 80, 0.10)';
  }

  const handlePrev = () => {
    setCurrent((prev) => (prev - 1 + bannersToShow.length) % bannersToShow.length);
  };
  const handleNext = () => {
    setCurrent((prev) => (prev + 1) % bannersToShow.length);
  };

  // Function to render message with clickable links
  const renderMessageWithLinks = (message: string) => {
    const elements: JSX.Element[] = [];
    let lastIndex = 0;
    
    // Regex for markdown links [text](url) and plain URLs
    const markdownLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g;
    const plainUrlRegex = /https?:\/\/[^\s]+/g;
    
    // Find all markdown links first
    const markdownMatches: Array<{ start: number; end: number; text: string; url: string }> = [];
    let match;
    
    while ((match = markdownLinkRegex.exec(message)) !== null) {
      markdownMatches.push({
        start: match.index,
        end: match.index + match[0].length,
        text: match[1],
        url: match[2],
      });
    }
    
    // Process message in segments
    for (let i = 0; i < markdownMatches.length; i++) {
      const mdMatch = markdownMatches[i];
      
      // Add text/URLs before this markdown link
      if (mdMatch.start > lastIndex) {
        const segment = message.substring(lastIndex, mdMatch.start);
        elements.push(...processPlainUrls(segment, elements.length));
      }
      
      // Add markdown link
      elements.push(
        <Link
          key={elements.length}
          href={mdMatch.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          sx={{ color: '#0066cc', textDecoration: 'underline', fontWeight: typographyTokens.fontWeights.bold, cursor: 'pointer', '&:hover': { color: '#004499' } }}
        >
          {mdMatch.text}
        </Link>
      );
      
      lastIndex = mdMatch.end;
    }
    
    // Process remaining text after last markdown link
    if (lastIndex < message.length) {
      const segment = message.substring(lastIndex);
      elements.push(...processPlainUrls(segment, elements.length));
    }
    
    return elements.length > 0 ? elements : message;
    
    // Helper function to process plain URLs in a text segment
    function processPlainUrls(text: string, startKey: number): JSX.Element[] {
      const parts = text.split(plainUrlRegex);
      const urls = text.match(plainUrlRegex) || [];
      const result: JSX.Element[] = [];
      
      parts.forEach((part, i) => {
        if (part) {
          result.push(<span key={`${startKey}-${i}-text`}>{part}</span>);
        }
        if (urls[i]) {
          result.push(
            <Link
              key={`${startKey}-${i}-url`}
              href={urls[i]}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              sx={{ color: '#0066cc', textDecoration: 'underline', fontWeight: typographyTokens.fontWeights.bold, cursor: 'pointer', '&:hover': { color: '#004499' } }}
            >
              {urls[i]}
            </Link>
          );
        }
      });
      
      return result;
    }
  };

  return (
  <Box sx={{ width: '100%', minWidth: 340, mb: 2, ...sx }}>
      <Box
        sx={{
          background: bgGradient,
          border: `3px solid ${borderColor}`,
          borderRadius: 10,
          boxShadow: boxShadow,
          px: 0,
          py: 0,
          display: 'flex',
          alignItems: 'center',
          minHeight: 48,
          width: '100%',
          minWidth: 340,
          position: 'relative',
          transition: 'opacity 0.5s',
          opacity: 1,
          animation: 'fadeInBanner 0.7s',
          zIndex: 1200,
          // marginBottom removed to eliminate gap
        }}
      >
        <Button
          variant="outlined"
          size="small"
          sx={{
            minWidth: 0,
            px: 1,
            py: 0.2,
            fontSize: typographyTokens.fontSizes.sm,
            position: 'absolute',
            top: -36,
            right: 0,
            zIndex: 1,
            borderRadius: '6px',
            border: `1.5px solid ${theme.palette.primeAdditional.midBlue}`,
            color: theme.palette.primeAdditional.midBlue,
          }}
          onClick={() => setHideBanners(true)}
        >
          Hide Banners
        </Button>
        {bannersToShow.length > 1 && (
          <IconButton
            onClick={handlePrev}
            sx={{
              width: 32,
              height: 32,
              minWidth: 32,
              m: 1,
              color: '#222',
              borderRadius: '50%',
              '&:hover': { backgroundColor: 'rgba(34, 34, 34, 0.12)' },
            }}
            aria-label="Previous banner"
            title="Previous banner"
          >
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
        )}
        <Box
          sx={{
            fontWeight: typographyTokens.fontWeights.bold,
            fontSize: { xs: 15, sm: 16 },
            color: '#222',
            lineHeight: 1.5,
            wordBreak: 'break-word',
            textAlign: 'center',
            flex: 1,
          }}
        >
          {renderMessageWithLinks(banner.value.replace(' (active)', ''))}
        </Box>
        {bannersToShow.length > 1 && (
          <IconButton
            onClick={handleNext}
            sx={{
              width: 32,
              height: 32,
              minWidth: 32,
              m: 1,
              color: '#222',
              borderRadius: '50%',
              '&:hover': { backgroundColor: 'rgba(34, 34, 34, 0.12)' },
            }}
            aria-label="Next banner"
            title="Next banner"
          >
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}
